<template>
  <component
    :is="currentComponent"
    :to="to"
    class="main-button__btn"
    :class="buttonDynamicClass"
    :disabled="disabled"
    v-on="$listeners"
  >
    <span class="main-button__btn-text uppercase">
      <slot />
    </span>
  </component>
</template>

<script>
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { CUSTOM_HTML_TAG, HTML_TAG } from '@fmpedia/enums'
import {
  BUTTON_CLASS_BY_BUTTON_STYLE_TYPE,
  BUTTON_STYLE_TYPE
} from 'shared/AMainButton/enums'

export default {
  name: 'AMainButton',
  props: {
    fontSmall: propValidator([PROP_TYPES.BOOLEAN], false, false),
    borderRadiusSmall: propValidator([PROP_TYPES.BOOLEAN], false, false),
    disabled: propValidator([PROP_TYPES.BOOLEAN], false, false),
    to: propValidator([PROP_TYPES.STRING, PROP_TYPES.OBJECT], false, ''),
    buttonStyleType: propValidator(
      [PROP_TYPES.STRING],
      false,
      BUTTON_STYLE_TYPE.GENERAL
    )
  },
  computed: {
    buttonDynamicClass() {
      return {
        'font-sm': this.fontSmall,
        'border-radius-sm': this.borderRadiusSmall,
        disabled: this.disabled,
        [BUTTON_CLASS_BY_BUTTON_STYLE_TYPE[this.buttonStyleType]]: true
      }
    },
    currentComponent() {
      return this.to ? CUSTOM_HTML_TAG.A_LINK : HTML_TAG.BUTTON
    }
  }
}
</script>

<style lang="scss" scoped>
$gradient-def-general: '<stop stop-color="%231A15CB"/><stop offset="0.5" stop-color="%238641E9"/><stop offset="1" stop-color="%2388E4FB"/>';
$gradient-def-error: '<stop stop-color="%23B4180E"/><stop offset="1" stop-color="%23F04438"/>';

$gradient-definition-by-gradient-type: (
  'general': $gradient-def-general,
  'error': $gradient-def-error
);

@function generate-svg-background(
  $border-radius,
  $gradient-direction,
  $gradient-type: 'general'
) {
  $gradient-direction-part: '';
  $gradient-definition-part: '';

  @if $gradient-direction == 'left' {
    $gradient-direction-part: 'x1="100%" y1="0%" x2="0%" y2="0%"';
  } @else if $gradient-direction == 'right' {
    $gradient-direction-part: 'x1="0%" y1="0%" x2="100%" y2="0%"';
  } @else {
    @return null;
  }

  $svg: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="Gradient" ' +
    $gradient-direction-part + ' gradientUnits="userSpaceOnUse">' +
    map-get($gradient-definition-by-gradient-type, $gradient-type) +
    '</linearGradient></defs><rect x="1" y="1" width="100%" height="100%" style="height:calc(100% - 2px);width:calc(100% - 2px)" rx="' +
    $border-radius + '" ry="' + $border-radius +
    '" stroke-width="2" fill="transparent" stroke="url(%23Gradient)"/></svg>';

  @return url($svg);
}

@mixin generateStylesForButtonType($class-name, $type) {
  &.#{$class-name} {
    background-image: generate-svg-background(35, 'left', $type);

    &:hover:not(.disabled) {
      background-image: generate-svg-background(35, 'right', $type);
    }

    &.border-radius-sm {
      background-image: generate-svg-background(23, 'left', $type);

      &:hover:not(.disabled) {
        background-image: generate-svg-background(23, 'right', $type);
      }
    }
  }
}

.main-button__btn {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $c--white;
  font-weight: 500;
  padding: 0;
  border: none;
  background-image: generate-svg-background(35, 'left');
  transition: background 0.3s linear;

  // allows setting non-transparent background
  border-radius: 80px;

  @include generateStylesForButtonType('main-button__btn--general', 'general');
  @include generateStylesForButtonType('main-button__btn--error', 'error');

  .main-button__btn-text {
    opacity: 0.9;
    font-size: 18px;
    line-height: 22px;
  }

  &.font-sm .main-button__btn-text {
    font-size: 16px;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
